import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, Plus, Trash2, Image as ImageIcon, Copy, Check, Download, UploadCloud, AlertCircle, Loader, Printer } from 'lucide-react';
import { downloadQrArtPng, downloadQrPng } from '../../utils/qrLabel';
import { collection, getDocs, doc, setDoc, updateDoc, query, where, arrayUnion, addDoc, serverTimestamp, writeBatch, onSnapshot, runTransaction } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, uploadString } from "firebase/storage";
import QRCode from 'qrcode';
import MaterialModal from '../MaterialModal';
import ConfirmDialog from './ConfirmDialog';
import { computeSheetNames, normalizeHriSearch, extractSheetNumber } from '../../lib/sheetNaming.mjs';
import { buildSheetScanCode, buildPieceScanCode } from '../../lib/scanCode';
import { collectPieceCodes } from '../../lib/pieces.mjs';
import { uploadsConfirmed, reserveSheetNumberAtomic } from '../../lib/sheetNumbering';
import { ChevronLeft, ChevronRight, FileDown } from 'lucide-react';

// Version del script de Illustrator que el ERP espera (public/scripts/Cartonbar_design_2.jsx).
// Unica fuente: se emite en el JSON (doDownloadJson) y se muestra en la nota de descarga.
const SCRIPT_VERSION = 17;

const FileUploader = ({ title, onPaste, onDrop, onDragOver, previewImage, onFileChange, uploadProgress, onDownloadJson, isDownloadDisabled, selectedFileName, hideJsonStep = false }) => {
    const fileInputId = `file-upload-${title.replace(/\s+/g, '-')}`;

    return (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{title} - Captura (Preview)</label>
                <div onDrop={onDrop} onDragOver={onDragOver} onPaste={onPaste} className="mt-1 flex justify-center items-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md relative h-40 bg-gray-50">
                    {previewImage ? (
                        <img src={previewImage} alt="Preview" className="max-h-full max-w-full object-contain" />
                    ) : (
                        <div className="space-y-1 text-center">
                            <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <p className="text-sm text-gray-500">Arrastra, pega o selecciona un archivo</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50">
                <h4 className="font-semibold text-gray-800">Archivo de Diseño Maestro</h4>
                
                {!hideJsonStep && (
                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Paso 1: Descargar JSON para script</label>
                    <button onClick={onDownloadJson} disabled={isDownloadDisabled} className="w-full flex items-center justify-center gap-2 text-sm px-3 py-2 border border-transparent rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
                        <Download size={16}/>
                        Descargar JSON de Archivo
                    </button>
                </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">{hideJsonStep ? 'Subir archivo final de Illustrator' : 'Paso 2: Subir archivo final de Illustrator'}</label>
                    <div className="flex items-center space-x-2 mt-1">
                        <label htmlFor={fileInputId} className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                            <UploadCloud size={16}/>
                            <span>Seleccionar...</span>
                        </label>
                        <input id={fileInputId} name={fileInputId} type="file" className="sr-only" onChange={onFileChange} />
                        <span className="text-sm text-gray-500 truncate" title={selectedFileName}>{selectedFileName || "Ningún archivo..."}</span>
                    </div>
                </div>

                {uploadProgress > 0 && (
                    <div className="w-full">
                        <div className="flex justify-between mb-1">
                            <span className="text-xs font-medium text-blue-700">Subiendo...</span>
                            <span className="text-xs font-medium text-blue-700">{Math.round(uploadProgress)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out" style={{ width: `${uploadProgress}%` }}></div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const PieceRow = ({ piece, onUpdate, onRemove, allPiecesMasterList, jobData, repetitions, onDownloadQrArt, onDownloadQrPng }) => {
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const imageInputRef = useRef(null);
    
    useEffect(() => {
        const generateQRCode = async () => {
            if (piece.code && jobData) {
                try {
                    const qrData = JSON.stringify({ jobId: jobData.id, pieceCode: piece.code });
                    const url = await QRCode.toDataURL(qrData, { width: 64, margin: 1 });
                    setQrCodeUrl(url);
                } catch (err) { console.error('Error generating QR code:', err); }
            } else { setQrCodeUrl(''); }
        };
        generateQRCode();
    }, [piece.code, jobData]);

    const handleCodeChange = (e) => {
        const value = e.target.value;
        onUpdate(piece.id, 'code', value);
        if (value.length > 1) {
            const filtered = allPiecesMasterList.filter(p => 
                p.code.toLowerCase().includes(value.toLowerCase()) || 
                (p.name && p.name.toLowerCase().includes(value.toLowerCase()))
            );
            setSuggestions(filtered);
        } else {
            setSuggestions([]);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        onUpdate(piece.id, 'code', suggestion.code);
        onUpdate(piece.id, 'name', suggestion.name);
        if (suggestion.previewUrl) {
            onUpdate(piece.id, 'previewUrl', suggestion.previewUrl);
        }
        setSuggestions([]);
    };

    const handleImageFileSelected = (e) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader();
            reader.onload = (event) => onUpdate(piece.id, 'previewUrl', event.target.result);
            reader.readAsDataURL(e.target.files[0]);
        }
    };
    
    const handlePasteImage = (e) => {
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf("image") !== -1) {
                const blob = items[i].getAsFile();
                const reader = new FileReader();
                reader.onload = (event) => onUpdate(piece.id, 'previewUrl', event.target.result);
                reader.readAsDataURL(blob);
                break;
            }
        }
    };
    const handleDropImage = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const reader = new FileReader();
            reader.onload = (event) => onUpdate(piece.id, 'previewUrl', event.target.result);
            reader.readAsDataURL(e.dataTransfer.files[0]);
        }
    };
    const handleDragOverImage = (e) => e.preventDefault();

    return (
        <tr className="align-middle">
            <td className="px-2 py-2 align-middle">
                <div 
                    onDrop={handleDropImage} 
                    onDragOver={handleDragOverImage} 
                    onPaste={handlePasteImage} 
                    onClick={() => imageInputRef.current.click()}
                    className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded flex items-center justify-center border-2 border-dashed border-gray-300 cursor-pointer"
                >
                    {piece.previewUrl ? 
                        <img src={piece.previewUrl} alt="Preview" className="max-h-full max-w-full object-contain" /> : 
                        <div className="text-xs text-gray-400 text-center flex flex-col items-center justify-center">
                            <UploadCloud size={18} className="mb-1"/>
                            Pegar/Buscar
                        </div>
                    }
                </div>
                 <input 
                    ref={imageInputRef}
                    type="file" 
                    className="hidden" 
                    accept="image/*" 
                    onChange={handleImageFileSelected}
                />
            </td>
            <td className="px-2 py-2 text-center align-middle"><div className="flex items-center justify-center gap-2"><div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded flex items-center justify-center">{qrCodeUrl ? <img src={qrCodeUrl} alt="QR Code" /> : <span className="text-xs text-gray-400">QR</span>}</div><div className="flex flex-col gap-1"><button onClick={() => onDownloadQrArt(piece)} className="p-1.5 text-gray-400 hover:text-blue-600" title="Descargar QR (PNG alta resolución) para el arte" disabled={!piece.code}><Download size={16}/></button><button onClick={() => onDownloadQrPng(piece)} className="p-1.5 text-gray-400 hover:text-green-600" title="Descargar etiqueta (PNG) para sticker térmico" disabled={!piece.code}><Printer size={16}/></button></div></div></td>
            <td className="px-2 py-2 align-middle"><div className="relative"><input type="text" placeholder="Código *" value={piece.code} onChange={handleCodeChange} className={`w-full p-1.5 border ${piece.codeError ? 'border-red-500' : (!piece.code?.trim() ? 'border-amber-400 bg-amber-50' : 'border-gray-300')} rounded-md text-sm`}/>{suggestions.length > 0 && (<ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-40 overflow-y-auto shadow-lg">{suggestions.map((s, index) => (<li key={index} onClick={() => handleSuggestionClick(s)} className="p-2 text-sm hover:bg-gray-100 cursor-pointer">{s.code} - {s.name}</li>))}</ul>)}</div></td>
            <td className="px-2 py-2 align-middle"><input type="text" placeholder="Nombre *" value={piece.name} onChange={(e) => onUpdate(piece.id, 'name', e.target.value)} className={`w-full p-1.5 border ${!piece.name?.trim() ? 'border-amber-400 bg-amber-50' : 'border-gray-300'} rounded-md text-sm`}/></td>
            <td className="px-2 py-2 text-center align-middle"><input type="number" min="1" value={piece.qtyPerSheet || 1} onChange={(e) => onUpdate(piece.id, 'qtyPerSheet', parseInt(e.target.value, 10) || 1)} className="w-20 p-1.5 border border-gray-300 rounded-md text-sm text-center"/></td>
            <td className="px-2 py-2 text-center align-middle font-medium">{(parseInt(piece.qtyPerSheet, 10) || 0) * (parseInt(repetitions, 10) || 0)}</td>
            <td className="px-2 py-2 text-center align-middle"><button onClick={() => onRemove(piece.id)} className="p-2 text-gray-400 hover:text-red-600"><Trash2 size={18}/></button></td>
        </tr>
    );
};

// El script de Illustrator asume mesa apaisada (sus presets de ancho son siempre mayores que
// los de alto — ver Cartonbar_design_2.jsx). El catálogo no garantiza ese orden: normaliza acá,
// una sola vez, para las dos fuentes posibles (Gang Run y material del catálogo).
const normalizeSheetOrientation = (width, height) => {
    if (width == null || height == null) return { width, height };
    return width >= height ? { width, height } : { width: height, height: width };
};

// Resuelve el tamaño de hoja (Gang Run > catálogo del material seleccionado) y normaliza
// orientación. MISMA función que usa el JSON (doDownloadJson) y el resumen del paso "Revisar"
// (jsonSummaryRows) — para que ambos muestren siempre el mismo número, en pulgadas.
const resolveSheetDimensionsIn = (jobData, materialInfo) => {
    let width = null, height = null;
    if (jobData?.sheet?.width && jobData?.sheet?.height) {
        const u = jobData.sheet.unit || 'in';
        const toIn = (v) => (u === 'mm' ? v / 25.4 : (u === 'ft' ? v * 12 : v));
        width = +toIn(Number(jobData.sheet.width)).toFixed(2);
        height = +toIn(Number(jobData.sheet.height)).toFixed(2);
    } else if (materialInfo?.dimensions?.width && materialInfo?.dimensions?.height) {
        // Solo unidades conocidas se convierten; cualquier otra (cm, ausente, vacía, etc.) deja
        // width/height en null — nunca pasa el número crudo.
        const KNOWN_UNIT_TO_IN = { in: (v) => v, mm: (v) => v / 25.4, ft: (v) => v * 12 };
        const toIn = KNOWN_UNIT_TO_IN[materialInfo.dimensions.unit];
        if (toIn) {
            width = +toIn(Number(materialInfo.dimensions.width)).toFixed(2);
            height = +toIn(Number(materialInfo.dimensions.height)).toFixed(2);
        }
    }
    return normalizeSheetOrientation(width, height);
};

const SheetModal = ({ isOpen, onClose, jobData: jobDataProp, initialSheet, user, db, storage, onSaveSuccess, taskId, viewMode = false, onAddBlockOrComponent }) => {
    const [jobData, setJobData] = useState(jobDataProp);
    const [selectedBlock, setSelectedBlock] = useState('');
    const [sheetNumber, setSheetNumber] = useState('');
    const [selectedMaterialId, setSelectedMaterialId] = useState('');
    const [repetitions, setRepetitions] = useState(1);
    const [description, setDescription] = useState('');
    const [isPrinting, setIsPrinting] = useState(false);
    const [hasCut, setHasCut] = useState(true); // ¿lleva corte? (independiente de impresión)
    const [printFaces, setPrintFaces] = useState(1);
    const [pieces, setPieces] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
    const [previewImageFront, setPreviewImageFront] = useState(null);
    const [previewImageBack, setPreviewImageBack] = useState(null);
    const [allPiecesMasterList, setAllPiecesMasterList] = useState([]);
    const [sheetId, setSheetId] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [status, setStatus] = useState('Listo para Prepress');
    const [designFileFront, setDesignFileFront] = useState(null);
    const [designFileBack, setDesignFileBack] = useState(null);
    const [uploadProgressFront, setUploadProgressFront] = useState(0);
    const [uploadProgressBack, setUploadProgressBack] = useState(0);
    const [uploadedFileFrontInfo, setUploadedFileFrontInfo] = useState(null);
    const [uploadedFileBackInfo, setUploadedFileBackInfo] = useState(null);
    const [isLoadingSheet, setIsLoadingSheet] = useState(false);
    const [currentInitialSheet, setCurrentInitialSheet] = useState(null);
    // Números de plancha ya reservados en esta sesión del modal (por bloque), para no quemar números al alternar bloques.
    const reservedNumbersRef = useRef({});
    // Asistente paso a paso (solo al CREAR). Diálogo propio para descargar JSON.
    const [wizardStep, setWizardStep] = useState(0);
    const [jsonConfirm, setJsonConfirm] = useState({ open: false, fileType: 'front' });

    const isUploading = useMemo(() => (uploadProgressFront > 0 && uploadProgressFront < 100) || (uploadProgressBack > 0 && uploadProgressBack < 100), [uploadProgressFront, uploadProgressBack]);
    // ¿Subidas confirmadas? Cubre el caso "archivo elegido pero upload fallido/cancelado" (info null,
    // isUploading false): el botón Guardar queda deshabilitado hasta confirmar (correlativo atómico).
    const uploadOk = useMemo(() => uploadsConfirmed({ designFileFront, uploadedFileFrontInfo, designFileBack, uploadedFileBackInfo }), [designFileFront, uploadedFileFrontInfo, designFileBack, uploadedFileBackInfo]);

    // Las piezas son obligatorias: cada fila debe tener código y nombre para poder alimentar el inventario.
    const piecesIncomplete = useMemo(() => pieces.length === 0 || pieces.some(p => !p.code?.trim() || !p.name?.trim()), [pieces]);

    // ¿El job tiene al menos un bloque "real" (con nombre)? El estado inicial de un job trae
    // siempre un bloque vacío {name:'', code:''} y hasBlocks=false, así que filtramos por nombre.
    // Si no hay bloques reales, la plancha se numera A NIVEL JOB (001, 002...) y el nombre/HRI
    // omite el código de bloque (ej. 1114_001).
    const hasUsableBlocks = useMemo(() => (jobData?.blocks || []).some(b => (b?.name || '').trim() !== ''), [jobData]);
    // PLANCHAS ÚNICAS (#5): los componentes NO agrupan la numeración — son las PIEZAS de la plancha
    // (se precargan). Solo los BLOQUES agrupan, para los pocos jobs que realmente los usan. Sin
    // bloques → numeración a nivel job (001, 002…), plancha única con sus piezas precargadas.
    const hasGroupings = hasUsableBlocks;
    // En un Gang Run, la plancha hereda el material del gang y las piezas (1UPs) ya vienen
    // precargadas; el operador no agrega piezas ni elige bloque — todo por default.
    const isGang = jobData?.type === 'GangRun';

    // Opciones del selector de agrupación: solo bloques (los componentes ya no agrupan).
    const groupingOptions = useMemo(() => ({
        blocks: (jobData?.blocks || []).filter(b => (b?.name || '').trim() !== ''),
        components: [],
    }), [jobData]);

    // Resuelve la agrupación seleccionada (bloque o componente) por su nombre, con su código.
    const selectedGrouping = useMemo(() => {
        if (!selectedBlock) return null;
        const b = (jobData?.blocks || []).find(x => x.name === selectedBlock);
        if (b) return { type: 'block', name: b.name, code: (b.code || '').trim() || b.name.replace(/\s+/g, '_').toUpperCase() };
        const c = (jobData?.components || []).find(x => x.name === selectedBlock);
        if (c) return { type: 'component', name: c.name, code: (c.code || '').trim() || c.name.replace(/\s+/g, '_').toUpperCase() };
        // Fallback: plancha existente cuyo bloque ya no está en las opciones (legado o renombrado).
        // Se usa el valor guardado tal cual para NO romper el nombre/cadena al editar.
        return { type: 'legacy', name: selectedBlock, code: selectedBlock.replace(/\s+/g, '_').toUpperCase() };
    }, [jobData, selectedBlock]);

    // Materiales seleccionables en los dropdowns del wizard: solo activos, MÁS el
    // material actualmente asignado a esta plancha aunque esté desactivado (si no,
    // abrir una plancha vieja sobre un material soft-deleted la deja sin selección).
    // Orden alfabético por displayName (agrupa por familia gratis: displayName
    // empieza por category).
    const selectableMaterials = useMemo(() => {
        return materials
            .filter(m => m.active !== false || m.id === selectedMaterialId)
            .slice()
            .sort((a, b) => (a.displayName || a.name || '').localeCompare(b.displayName || b.name || ''));
    }, [materials, selectedMaterialId]);
    const materialOptionLabel = (m) => `${m.displayName || m.name}${m.active === false ? ' (inactivo)' : ''}`;

    // Construye los nombres de archivo a partir de un número de plancha dado (o el actual del
    // estado). Delegado a la lib pura sheetNaming (§9.3 Pilar 1): la misma función la usan
    // los unit tests y el script de migración — UNA sola fuente del formato.
    const computeFileNames = useCallback((numberOverride) => {
        const num = numberOverride != null ? numberOverride : sheetNumber;
        if (!jobData || !num) return { front: '', back: '' };
        if (hasGroupings && !selectedGrouping) return { front: '', back: '' };
        return computeSheetNames({
            jobId: jobData.id,
            blockCode: hasGroupings ? selectedGrouping.code : '',
            sheetNumber: num,
            isPrinting,
            printFaces,
        });
    }, [jobData, sheetNumber, isPrinting, printFaces, hasGroupings, selectedGrouping]);

    const generatedFileName = useMemo(() => computeFileNames(), [computeFileNames]);

    // "Nombre de Plancha" (hri) = NOMBRE DE ARCHIVO FRONTAL (decisión §9.2: es lo que está
    // físicamente rotulado en planchas y archivos; el operador lee eso). El formato `#...`
    // de computeHri se eliminó — los datos viejos con `#` los renombra la migración F1-B2.
    const hri = generatedFileName.front;

    useEffect(() => { const fetchAllPieces = async () => { const piecesSnapshot = await getDocs(collection(db, "pieces")); setAllPiecesMasterList(piecesSnapshot.docs.map(doc => doc.data())); }; fetchAllPieces(); const materialsQuery = query(collection(db, "materials")); const unsubscribe = onSnapshot(materialsQuery, (snapshot) => { setMaterials(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); }); return () => unsubscribe(); }, [db]);
    
    const populateFormWithSheetData = useCallback((sheet) => {
        if (!sheet) return;
        setCurrentInitialSheet(sheet);
        setSheetId(sheet.id);
        setSelectedBlock(sheet.block || '');
        setSheetNumber(sheet.sheetNumber || '');
        setSelectedMaterialId(sheet.materialId || '');
        setRepetitions(sheet.repetitions || 1);
        setDescription(sheet.description || '');
        setIsPrinting(sheet.isPrinting || false);
        setHasCut(sheet.hasCut !== false); // legado: si no existe, se asume con corte
        setPrintFaces(sheet.printFaces || 1);
        setPieces(sheet.pieces.map(p => ({ ...p, id: Math.random().toString(36).substr(2, 9), qtyPerSheet: p.qtyPerSheet || 1, previewUrl: p.previewUrl || null })) || []);
        setPreviewImageFront(sheet.previewImageFrontUrl || null);
        setPreviewImageBack(sheet.previewImageBackUrl || null);
        setStatus(sheet.status || 'Listo para Prepress');
        setUploadedFileFrontInfo(sheet.designFileFront || null);
        setUploadedFileBackInfo(sheet.designFileBack || null);
    }, []);

    const initializeNewSheet = useCallback(() => {
        setCurrentInitialSheet(null);
        reservedNumbersRef.current = {};
        setWizardStep(0);
        const newId = doc(collection(db, "dummy")).id;
        setSheetId(newId);
        // Precarga las PIEZAS del job (#5: evitar teclear código/nombre): componentes del job,
        // o líneas del gang, o el SKU del Producto. Si no hay, una fila vacía como antes.
        const mk = (code, name, qty, preview) => ({ id: Math.random().toString(36).substr(2, 9), code: code || '', name: name || '', qtyPerSheet: qty || 1, previewUrl: preview || null });
        const comps = (jobData?.components || []).filter(c => (c?.name || '').trim() && (c?.code || '').trim());
        let initialPieces;
        if (comps.length) initialPieces = comps.map(c => mk(c.code, c.name, c.qtyPerProduct || 1, c.imageUrl));
        else if (jobData?.type === 'GangRun' && (jobData?.gangLines || []).length) initialPieces = jobData.gangLines.map(l => mk(l.sku || l.pieceCode, l.pieceName || l.sku, l.ups || 1, null));
        else if (jobData?.productSnapshot?.sku) initialPieces = [mk(jobData.productSnapshot.sku, jobData.productSnapshot.name || jobData.productSnapshot.sku, 1, jobData.coverImageUrl)];
        else initialPieces = [mk('', '', 1, null)];
        setPieces(initialPieces);
        // Solo los bloques agrupan; si no hay bloques, numeración a nivel job (plancha única).
        const firstBlock = (jobData?.blocks || []).find(b => (b?.name || '').trim() !== '');
        setSelectedBlock(firstBlock?.name || '');
        // Sin bloques: muestra 001 por default de una vez (el efecto async lo refina al siguiente real).
        setSheetNumber(firstBlock ? '' : 1);
        setSelectedMaterialId(jobData?.type === 'GangRun' ? (jobData?.materialId || '') : '');
        setRepetitions(1); setDescription(''); setIsPrinting(false); setHasCut(true); setPrintFaces(1);
        setPreviewImageFront(null); setPreviewImageBack(null); setDesignFileFront(null); setDesignFileBack(null);
        setStatus('Listo para Prepress');
    }, [db, jobData]);

    useEffect(() => {
        if (initialSheet) {
            populateFormWithSheetData(initialSheet);
        } else {
            initializeNewSheet();
        }
    }, [initialSheet, populateFormWithSheetData, initializeNewSheet]);
    
    const getNumberingKey = useCallback(() => {
        // Sin agrupaciones (ni bloques) → un único contador a nivel job.
        // OJO: el id NO puede ser '__JOB__' — Firestore reserva los ids con doble guion bajo
        // al inicio Y fin (regex __.*__) y la transacción del contador falla. Usamos 'JOB'.
        if (!hasGroupings) return 'JOB';
        // Clave segura para la ruta del contador (puede tener espacios o "/").
        return ((selectedGrouping?.code) || (selectedGrouping?.name) || selectedBlock || '').toString().replace(/[/\\]/g, '_');
    }, [hasGroupings, selectedGrouping, selectedBlock]);

    // Máximo sheetNumber YA existente en Firebase para el bloque actual (o para el job si no hay
    // bloques). Empareja por NOMBRE o CÓDIGO de bloque para no fallar si las planchas viejas se
    // guardaron con uno u otro valor. Recibe el snapshot de TODAS las planchas del job.
    const computeExistingMax = useCallback((allSheetsSnap) => {
        const code = selectedGrouping?.code;
        let maxNum = 0;
        allSheetsSnap.forEach(d => {
            // TRINQUETE (M1-B) — NO SIMPLIFICAR: la plancha que se está editando NUNCA debe
            // contarse en su propio máximo. Si su sheetNumber entra en el cálculo, cada
            // reapertura la empuja un número más arriba y el desfase se acumula sin tope
            // (J-1134: 010 → 017 → 018 sobre el MISMO documento). Excluirse a sí misma hace
            // ese lazo imposible por construcción, no solo improbable.
            // Al CREAR, el sheetId aún no existe en Firestore → esta línea no descarta nada
            // y el número provisional se calcula igual que siempre.
            if (d.id === sheetId) return;
            const s = d.data();
            // Con agrupación: solo las planchas de ESTE bloque/componente (por nombre o código).
            // Sin agrupaciones: TODAS las planchas del job comparten una única serie, sin importar
            // qué valor tenga su campo `block` (cubre planchas legadas creadas saltándose el paso).
            const belongs = hasGroupings
                ? (s.block === selectedBlock || (!!code && s.block === code))
                : true;
            if (belongs) {
                const n = Number(s.sheetNumber) || 0;
                if (n > maxNum) maxNum = n;
            }
        });
        return maxNum;
    }, [hasGroupings, selectedBlock, selectedGrouping, sheetId]);

    // Muestra un número PROVISIONAL (solo lectura, sin escribir nada) al abrir el modal / elegir bloque.
    // El número DEFINITIVO se reserva atómicamente SOLO al GUARDAR la plancha (handleSave). Así:
    //   - descargar el JSON, generar QR o subir archivos durante las pruebas NO quema números, y
    //   - no hay duplicados, porque la reserva real al guardar es una transacción monótona por bloque.
    useEffect(() => {
        let cancelled = false;
        const previewSheetNumber = async () => {
            // CARRERA M1-A — NO SIMPLIFICAR: la guarda va sobre la PROP `initialSheet`, NO sobre
            // el estado `currentInitialSheet`. Ese estado lo puebla OTRO efecto
            // (populateFormWithSheetData) y en el PRIMER RENDER de cada montaje todavía vale null,
            // aunque la prop ya venga poblada. Con la guarda sobre el estado, este efecto —pensado
            // solo para CREAR— se colaba en modo EDICIÓN y pisaba el sheetNumber correcto con
            // (máximo del job + 1), renombrando planchas ya rotuladas y dejando huérfano el
            // archivo ya subido. La prop está poblada desde el primer render → cierra la ventana
            // en su origen, no después.
            if (initialSheet) return;
            if (hasGroupings && !selectedGrouping) return; // con agrupaciones, hay que elegir una primero
            if (!jobData?.id || !db) {
                // Sin acceso a Firebase (p. ej. job aún sin guardar): por defecto 001.
                if (!hasGroupings) setSheetNumber(1);
                return;
            }
            const key = getNumberingKey();
            if (reservedNumbersRef.current[key]) { setSheetNumber(reservedNumbersRef.current[key]); return; }
            try {
                // El número provisional = (máximo que YA existe) + 1. NO se usa el contador
                // para mostrar: así, abrir/cerrar el modal sin guardar NO cambia el número
                // (antes el contador inflado por números mostrados-pero-no-guardados causaba saltos).
                const sheetsRef = collection(db, `jobs/${jobData.id}/sheets`);
                const allSheetsSnap = await getDocs(sheetsRef);
                // 2ª guarda (M1-A): si el modal cambió de modo/bloque/job mientras la consulta
                // estaba EN VUELO, este resultado ya no aplica. OJO: releer `initialSheet` acá
                // NO sirve — el closure async conserva el valor del render que lo creó, así que
                // devolvería lo mismo que antes del await. El flag de cleanup SÍ funciona, porque
                // React ejecuta la limpieza del efecto anterior antes de volver a ejecutarlo.
                if (cancelled) return;
                const existingMax = computeExistingMax(allSheetsSnap);
                setSheetNumber(existingMax + 1);
            } catch (error) {
                console.error("Error calculando número provisional de plancha:", error);
            }
        };
        previewSheetNumber();
        return () => { cancelled = true; };
    }, [selectedBlock, jobData, db, initialSheet, hasGroupings, selectedGrouping, getNumberingKey, computeExistingMax]);

    // Reserva atómica del número definitivo. Idempotente por bloque dentro de la sesión del modal:
    // si ya se reservó, devuelve el mismo número sin volver a incrementar el contador.
    const ensureSheetNumberReserved = useCallback(async () => {
        if (currentInitialSheet) return Number(sheetNumber);
        if (!jobData?.id || !db) return Number(sheetNumber);
        if (hasGroupings && !selectedGrouping) return Number(sheetNumber);

        const key = getNumberingKey();
        if (reservedNumbersRef.current[key]) return reservedNumbersRef.current[key];

        // Número GAP-FREE y AUTO-SANEABLE: SIEMPRE (máximo que ya existe) + 1, leído fresco
        // de Firebase al guardar. No se deja inflar por números que se mostraron pero nunca se
        // guardaron (esa era la causa de los saltos, p. ej. 001, 002, 006). El contador queda
        // solo como registro, sincronizado a este valor.
        const sheetsRef = collection(db, `jobs/${jobData.id}/sheets`);
        const allSheetsSnap = await getDocs(sheetsRef);
        const existingMax = computeExistingMax(allSheetsSnap);
        // Reserva ATÓMICA: max(existingMax, contador)+1 DENTRO de runTransaction sobre el doc
        // contador → dos guardados simultáneos serializan y toman números distintos (no colisión).
        const assigned = await reserveSheetNumberAtomic({
            db, jobId: jobData.id, key, block: hasGroupings ? selectedBlock : '(sin agrupación)',
            existingMax, fb: { doc, runTransaction },
        });

        reservedNumbersRef.current[key] = assigned;
        setSheetNumber(assigned);
        return assigned;
    }, [currentInitialSheet, selectedBlock, jobData, db, sheetNumber, hasGroupings, selectedGrouping, getNumberingKey, computeExistingMax]);

    const handleFileChange = (setter) => (e) => { if (e.target.files && e.target.files[0]) { setter(e.target.files[0]); } };
    const handlePaste = (setter) => (e) => { const items = e.clipboardData.items; for (let i = 0; i < items.length; i++) { if (items[i].type.indexOf("image") !== -1) { const blob = items[i].getAsFile(); const reader = new FileReader(); reader.onload = (event) => setter(event.target.result); reader.readAsDataURL(blob); break; } } };
    const handleDrop = (setter) => (e) => { e.preventDefault(); if (e.dataTransfer.files && e.dataTransfer.files[0]) { const reader = new FileReader(); reader.onload = (event) => setter(event.target.result); reader.readAsDataURL(e.dataTransfer.files[0]); } };
    const handleDragOver = (e) => e.preventDefault();

    // Guarda un material NUEVO creado desde el "+" del SheetModal. Antes el MaterialModal
    // no recibía onSave aquí → el botón "Guardar Material" no hacía nada. La lista de
    // materiales se refresca sola (onSnapshot) y dejamos el nuevo material seleccionado.
    const handleSaveMaterialInline = async (data, imageFile) => {
        try {
            let imageUrl = data.imageUrl || '';
            if (imageFile && storage) {
                const r = ref(storage, `materials/${Date.now()}_${imageFile.name}`);
                await new Promise((res, rej) => { const t = uploadBytesResumable(r, imageFile); t.on('state_changed', null, rej, res); });
                imageUrl = await getDownloadURL(r);
            }
            const refDoc = await addDoc(collection(db, 'materials'), { ...data, imageUrl, stock: { quantity: 0, location: '', minStockLevel: 0, notes: '' }, createdAt: serverTimestamp() });
            setSelectedMaterialId(refDoc.id);
            setIsMaterialModalOpen(false);
        } catch (e) { console.error('Error guardando material:', e); alert('No se pudo guardar el material: ' + (e?.message || e)); }
    };
    const addPiece = () => { setPieces([...pieces, { id: Math.random().toString(36).substr(2, 9), code: '', name: '', qtyPerSheet: 1, previewUrl: null }]); };
    const updatePiece = (id, field, value) => { const newPieces = pieces.map(p => { if (p.id === id) { const updatedPiece = { ...p, [field]: value }; if (field === 'code') { const isDuplicate = pieces.some(otherPiece => otherPiece.id !== id && otherPiece.code.toLowerCase() === value.toLowerCase() && value !== ''); updatedPiece.codeError = isDuplicate; } return updatedPiece; } return p; }); setPieces(newPieces); };
    const removePiece = (id) => { setPieces(pieces.filter(p => p.id !== id)); };
    const getFullFileName = (baseName, fileObject) => { if (!baseName || !fileObject) return null; const extension = fileObject.name.split('.').pop(); return `${baseName}.${extension}`; };
    const uploadPreviewImage = async (image, fileName) => { if (!image || typeof image !== 'string' || !image.startsWith('data:image')) return image; const storageRef = ref(storage, `jobs/${jobData.id}/previews/${fileName}.png`); await uploadString(storageRef, image, 'data_url'); return getDownloadURL(storageRef); };
    // Identificador del QR: nombre de plancha (sin #) + código de pieza, ej. "1110-COR_001_RTG".
    // Reserva el número definitivo para que el sticker coincida con la plancha que se guardará.
    const buildPieceQrLabel = async (piece) => {
        // Usa el número provisional (NO reserva): generar el QR de prueba no debe quemar
        // números. El número definitivo se fija al guardar la plancha.
        const assignedNumber = Number(sheetNumber);
        const plateName = computeFileNames(assignedNumber).front || '';
        const jobNum = jobData.id.replace('J-', '');
        return plateName ? `${plateName}_${piece.code}` : `${jobNum}_${piece.code}`;
    };
    const handleDownloadQrArt = async (piece) => {
        if (!piece.code || !jobData) { alert("Faltan datos para generar el QR."); return; }
        try {
            const label = await buildPieceQrLabel(piece);
            await downloadQrArtPng(buildPieceScanCode({ jobId: jobData.id, pieceCode: piece.code }), label, `${label}_QR.png`);
        } catch (e) { console.error("Error generando QR PNG:", e); alert("No se pudo generar el QR."); }
    };
    const handleDownloadQrPng = async (piece) => {
        if (!piece.code || !jobData) { alert("Faltan datos para generar el QR."); return; }
        try {
            const label = await buildPieceQrLabel(piece);
            await downloadQrPng(buildPieceScanCode({ jobId: jobData.id, pieceCode: piece.code }), label, `${label}_QR.png`);
        } catch (e) { console.error("Error generando QR PNG:", e); alert("No se pudo generar la etiqueta."); }
    };
    // Abre el diálogo propio (modo edición) que muestra el resumen del JSON antes de descargar.
    const openJsonConfirm = (fileType) => setJsonConfirm({ open: true, fileType });
    const doDownloadJson = async (fileType) => {
        if (!sheetId) { alert("Error: No se ha podido generar un ID para la plancha."); return; }
        try {
            // Fija (reserva) el número AHORA para que el nombre del JSON sea el definitivo.
            // La confirmación/resumen la maneja la UI (paso "Revisar" del asistente o el
            // diálogo propio en modo edición) ANTES de llamar aquí.
            const assigned = await ensureSheetNumberReserved();
            const names = computeFileNames(assigned);
            const fileNameToUse = fileType === 'front' ? names.front : names.back;
            if (!fileNameToUse) { alert("No hay un nombre de archivo generado. Completa los datos de la plancha."); return; }
            // Contrato v1 de scanCode.js (§9.3 Pilar 2): el QR lleva j/id/hri — FileFinder
            // y /scan lo resuelven directo por ID (y siguen aceptando los QR ya impresos).
            const qrContent = buildSheetScanCode({ jobId: jobData.id, sheetDocId: sheetId, hri: fileNameToUse });
            const qrCodeBase64 = await QRCode.toDataURL(qrContent);
            const materialInfo = materials.find(m => m.id === selectedMaterialId);
            // Logo de CARTONBAR (base64) para que el script lo coloque. Si falla, se omite.
            let logoBase64 = '';
            try {
                const resp = await fetch(`${process.env.PUBLIC_URL}/LOGO%20CARTONBAR_NEGRO.png`);
                if (resp.ok) {
                    const blob = await resp.blob();
                    logoBase64 = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).split(',')[1]); fr.onerror = () => res(''); fr.readAsDataURL(blob); });
                }
            } catch (e) { /* sin logo */ }
            // Tamaño de hoja: 1) Gang Run (jobData.sheet) tiene precedencia; 2) si no, fallback
            // a las dimensiones del material del catálogo. Ya viene en PULGADAS y con la
            // orientación normalizada (lado mayor = width) — misma función que usa el resumen
            // del paso "Revisar" (jsonSummaryRows), para que ambos coincidan siempre.
            const { width: sheetWidthIn, height: sheetHeightIn } = resolveSheetDimensionsIn(jobData, materialInfo);
            const jsonData = {
                hri: fileNameToUse, fileName: fileNameToUse, materialName: materialInfo?.name || 'N/A', repetitions: Number(repetitions),
                qrCodeBase64: qrCodeBase64.split(',')[1], qrSizeInches: 0.75,
                isPrinting: !!isPrinting,
                logoBase64,
                scriptVersionRequired: SCRIPT_VERSION,
                sheetWidth: sheetWidthIn,
                sheetHeight: sheetHeightIn,
                pieces: pieces.filter(p => p.code).map(p => ({ code: p.code, name: p.name || '', quantity: p.qtyPerSheet || 1, }))
            };
            const jsonString = JSON.stringify(jsonData, null, 2);
            const blob = new Blob([jsonString], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${fileNameToUse}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to generate JSON file for Illustrator script', err);
            alert("Hubo un error al generar el archivo JSON: " + (err?.message || err));
        }
    };
    useEffect(() => {
        const upload = (file, progressSetter, infoSetter, fileName) => {
            if (!file || !fileName) return;
            progressSetter(0.1);
            const storageRef = ref(storage, `jobs/${jobData.id}/designs/${fileName}`);
            const uploadTask = uploadBytesResumable(storageRef, file);
            uploadTask.on('state_changed', (snapshot) => progressSetter((snapshot.bytesTransferred / snapshot.totalBytes) * 100), (error) => { console.error("Upload failed:", error); alert(`Error al subir archivo: ${error.message}`); progressSetter(0); }, async () => { const downloadURL = await getDownloadURL(uploadTask.snapshot.ref); infoSetter({ name: fileName, url: downloadURL, size: file.size, type: file.type }); });
        };
        if (designFileFront) { upload(designFileFront, setUploadProgressFront, setUploadedFileFrontInfo, getFullFileName(generatedFileName.front, designFileFront)); }
    }, [designFileFront, generatedFileName.front, jobData, storage]);
    useEffect(() => {
        const upload = (file, progressSetter, infoSetter, fileName) => { 
            if (!file || !fileName) return; 
            progressSetter(0.1); 
            const storageRef = ref(storage, `jobs/${jobData.id}/designs/${fileName}`); 
            const uploadTask = uploadBytesResumable(storageRef, file); 
            uploadTask.on('state_changed', (snapshot) => progressSetter((snapshot.bytesTransferred / snapshot.totalBytes) * 100), (error) => { console.error("Upload failed:", error); alert(`Error al subir archivo: ${error.message}`); progressSetter(0); }, async () => { const downloadURL = await getDownloadURL(uploadTask.snapshot.ref); infoSetter({ name: fileName, url: downloadURL, size: file.size, type: file.type }); }); 
        };
        if (designFileBack) { upload(designFileBack, setUploadProgressBack, setUploadedFileBackInfo, getFullFileName(generatedFileName.back, designFileBack)); }
    }, [designFileBack, generatedFileName.back, jobData, storage]);

    const handleSave = async () => {
        if (!sheetId) { alert("Error: No se ha podido generar un ID para la plancha."); return; }

        // Al CREAR: si el job tiene bloques o componentes, la plancha DEBE pertenecer a uno.
        // Al editar/corregir el bloque está bloqueado, así que no se revalida.
        if (!currentInitialSheet && hasGroupings && !selectedGrouping) {
            alert("Debes seleccionar el bloque o componente al que pertenece esta plancha.");
            return;
        }

        // Validación obligatoria de piezas: sin esto no se puede alimentar el inventario.
        if (pieces.length === 0 || pieces.every(p => !p.code?.trim())) {
            alert("Debes registrar al menos una pieza con su código y nombre antes de guardar. Esta información es obligatoria para alimentar el inventario.");
            return;
        }
        if (pieces.some(p => !p.code?.trim() || !p.name?.trim())) {
            alert("Cada pieza debe tener código y nombre. Completa o elimina las filas incompletas (resaltadas en amarillo) antes de guardar.");
            return;
        }
        if (pieces.some(p => p.codeError)) {
            alert("Hay códigos de pieza duplicados. Corrígelos antes de guardar.");
            return;
        }

        // Confirmación de resumen: estado (Listo para Prepress / Hold), impresión o solo
        // corte, y la cantidad (repeticiones). Evita guardar con datos equivocados.
        const estadoTxt = (taskId ? 'Corregido por Diseño' : status);
        const impresionTxt = isPrinting ? `Impresión a ${printFaces} cara(s)` : 'Solo corte (sin impresión)';
        const piezasTxt = pieces.filter(p => p.code?.trim()).length;
        const confirmMsg =
            `Confirma la plancha antes de guardar:\n\n` +
            `• Estado: ${estadoTxt}\n` +
            `• ${impresionTxt}\n` +
            `• Cantidad (repeticiones): ${repetitions}\n` +
            `• Piezas: ${piezasTxt}\n\n` +
            `¿Guardar la plancha?`;
        if (!window.confirm(confirmMsg)) return;

        // ORDEN ESTRICTO (fix correlativo atómico): (1) subir archivo → (2) confirmar subida OK →
        // (3) recién entonces reservar número + escribir el doc. Si una subida quedó pendiente o
        // falló, NO se reserva número ni se escribe el doc (no se quema un número del correlativo).
        if (!uploadsConfirmed({ designFileFront, uploadedFileFrontInfo, designFileBack, uploadedFileBackInfo })) {
            alert('Esperá a que termine la subida del archivo de diseño antes de guardar (la subida no está confirmada).');
            return;
        }

        setIsSaving(true);
        try {
            // Asegura el número definitivo (reserva atómica si aún era provisional) y deriva
            // nombres/HRI con ESE número, para que todo quede consistente aunque no se haya
            // tocado antes la sección de archivos.
            const finalNumber = await ensureSheetNumberReserved();
            const finalNames = computeFileNames(finalNumber);

            // Identidad de la plancha (§9.3 Pilar 1). Regla de edición:
            // - Sin marcas de producción → el hri se regenera con los datos actuales
            //   (cubre N↔P↔A y converge los datos `#…` viejos al editarlos).
            // - Con producción registrada → se PRESERVA el hri (es lo rotulado en piso)
            //   y se avisa si el recalculado difiere.
            const computedHri = finalNames.front;
            let finalHri;
            let finalSheetNumber = Number(finalNumber);
            let hriNotice = '';
            if (!currentInitialSheet) {
                finalHri = computedHri;
            } else {
                const oldHri = currentInitialSheet.hri || '';
                const hasMarks = (currentInitialSheet.printedCount || 0) > 0
                    || (currentInitialSheet.printedCountBack || 0) > 0
                    || (currentInitialSheet.cutCount || 0) > 0;
                if (!hasMarks) {
                    finalHri = computedHri || oldHri;
                } else {
                    finalHri = oldHri || computedHri;
                    if (oldHri && computedHri && oldHri !== computedHri) {
                        hriNotice = `La plancha ya tiene producción registrada: se conserva el nombre "${oldHri}". (Con los datos nuevos sería "${computedHri}".)`;
                    }
                    // Guarda del invariante D5 (§9.4): en una plancha con marcas se PRESERVA
                    // el hri rotulado en piso, pero `sheetNumber` pudo derivar de la
                    // renumeración gap-free. Reconciliamos `sheetNumber` AL número embebido
                    // en el hri preservado (la etiqueta física manda, §9.2) — solo para ESTA
                    // plancha, sin renombrar ni tocar otras. La restauración masiva es deuda Fase 2.
                    const hriNum = extractSheetNumber(finalHri);
                    if (hriNum != null && hriNum !== finalSheetNumber) {
                        const driftNotice = `El número del nombre físico (${String(hriNum).padStart(3, '0')}) no coincide con sheetNumber=${finalSheetNumber}. Se conserva el nombre rotulado "${finalHri}" y se reconcilia sheetNumber a ${hriNum}.`;
                        hriNotice = hriNotice ? `${hriNotice}\n\n${driftNotice}` : driftNotice;
                        finalSheetNumber = hriNum;
                    }
                }
            }
            if (!finalHri) throw new Error('No se pudo derivar el nombre (hri) de la plancha. Verifica bloque y número.');

            // Guarda de colisión (no debería ocurrir con la reserva atómica; si ocurre
            // → error, no sobrescribir).
            const dupSnap = await getDocs(query(collection(db, `jobs/${jobData.id}/sheets`), where('hri', '==', finalHri)));
            const dup = dupSnap.docs.find(d => d.id !== sheetId);
            if (dup) throw new Error(`Colisión de identidad: ya existe otra plancha "${finalHri}" en este job. No se guardó nada — reporta esto (no debería pasar con la numeración atómica).`);

            const previewFrontUrl = await uploadPreviewImage(previewImageFront, finalNames.front);
            const previewBackUrl = await uploadPreviewImage(previewImageBack, finalNames.back);

            const piecesWithUrls = await Promise.all(
                pieces.map(async (piece) => {
                    const piecePreviewUrl = await uploadPreviewImage(piece.previewUrl, `${finalHri}_${piece.code}`);
                    return { ...piece, previewUrl: piecePreviewUrl };
                })
            );

            const piecesMasterRef = collection(db, "pieces");
            for (const piece of piecesWithUrls) { if (piece.code && piece.name) { const existingPiece = allPiecesMasterList.find(p => p.code.toLowerCase() === piece.code.toLowerCase()); if (!existingPiece) { const newPieceData = { code: piece.code, name: piece.name }; if (piece.previewUrl) { newPieceData.previewUrl = piece.previewUrl; } await addDoc(piecesMasterRef, newPieceData); } else if (piece.previewUrl && !existingPiece.previewUrl) { const pieceDocQuery = query(piecesMasterRef, where("code", "==", piece.code)); const pieceDocs = await getDocs(pieceDocQuery); if (!pieceDocs.empty) { const pieceDocRef = pieceDocs.docs[0].ref; await updateDoc(pieceDocRef, { previewUrl: piece.previewUrl }); } } } }

            let finalStatus; let finalPrepressStatus;
            if (taskId) { finalStatus = 'Corregido por Diseño'; finalPrepressStatus = 'Corregido por Diseño'; } else { finalStatus = status; finalPrepressStatus = 'Pendiente'; }

            const sheetData = {
                jobId: jobData.id, block: selectedBlock, sheetNumber: finalSheetNumber, materialId: selectedMaterialId, repetitions: Number(repetitions), description: description, isPrinting: isPrinting, hasCut: hasCut, printFaces: isPrinting ? printFaces : null,
                pieces: piecesWithUrls.map(({id, codeError, ...rest}) => ({...rest})),
                previewImageFrontUrl: previewFrontUrl, previewImageBackUrl: previewBackUrl,
                designFileFront: uploadedFileFrontInfo || currentInitialSheet?.designFileFront || null,
                designFileBack: uploadedFileBackInfo || currentInitialSheet?.designFileBack || null,
                status: finalStatus, hri: finalHri, prepressStatus: finalPrepressStatus,
                // Campo de búsqueda (§9.3 Pilar 1): SIEMPRE se escribe en cada save.
                // Case-insensitive y sin '#' por diseño — lo consume la collection-group
                // query de /scan y FileFinder (Pilar 3).
                hriSearch: normalizeHriSearch(finalHri),
                // Denormalización de códigos de pieza (W1 · B1b): array en minúsculas
                // para la búsqueda global de PIEZAS con UNA collection-group query
                // (`pieceCodes` array-contains), igual que hriSearch para planchas.
                pieceCodes: collectPieceCodes(piecesWithUrls),
            };

            const sheetRef = doc(db, `jobs/${jobData.id}/sheets`, sheetId);
            const batch = writeBatch(db);

            // Detalle del cambio para el log/auditoría (y para alimentar a la IA del job).
            const materialName = materials.find(m => m.id === selectedMaterialId)?.name || 'N/A';
            const printDetail = isPrinting ? `${printFaces} cara(s)` : 'solo corte';
            const details = `${finalHri || finalNames.front || 'Plancha'} · ${selectedGrouping ? selectedGrouping.name : 'sin bloque'} · ${materialName} · ${printDetail} · ${pieces.length} pieza(s) [${pieces.map(p => p.code).filter(Boolean).join(', ')}] · estado: ${finalStatus}`;

            if (currentInitialSheet) {
                const action = finalStatus === 'Corregido por Diseño' ? 'Plancha Corregida' : 'Plancha Actualizada';
                const historyEntry = { action, details, user: user.email, timestamp: new Date() };
                batch.update(sheetRef, { ...sheetData, history: arrayUnion(historyEntry) });
            } else {
                const historyEntry = { action: 'Plancha Creada', details, user: user.email, timestamp: new Date() };
                batch.set(sheetRef, { ...sheetData, createdBy: user.uid, createdByName: user.displayName || user.email, history: [historyEntry] });
            }

            if(taskId) {
                const taskRef = doc(db, `jobs/${jobData.id}/tasks`, taskId);
                batch.update(taskRef, { status: 'completed', completedAt: serverTimestamp(), completedBy: user.email });
            }
            
            await batch.commit();
            if (hriNotice) alert(hriNotice);
            if(onSaveSuccess) onSaveSuccess();
            onClose();

        } catch (error) { console.error("Error al guardar:", error); alert(`Ocurrió un error: ${error.message}`);
        } finally { setIsSaving(false); }
    };
    
    if (!isOpen || !jobData) return null;

    // ── Asistente paso a paso (solo al CREAR plancha nueva) ───────────────────
    const isWizard = !currentInitialSheet && !viewMode && !taskId;
    const wizardSteps = [
        ...(hasGroupings ? [{ key: 'bloque', label: 'Bloque' }] : []),
        { key: 'material', label: 'Material' },
        { key: 'piezas', label: 'Piezas' },
        { key: 'reps', label: 'Cantidad' },
        { key: 'impresion', label: 'Impresión' },
        { key: 'revisar', label: 'Revisar / JSON' },
        { key: 'subir', label: 'Subir archivo' },
    ];
    const curStepIdx = Math.min(wizardStep, wizardSteps.length - 1);
    const curKey = wizardSteps[curStepIdx]?.key;
    const stepValid = (key) => {
        switch (key) {
            case 'bloque': return !!selectedGrouping;
            case 'material': return !!selectedMaterialId;
            case 'piezas': return !piecesIncomplete && !pieces.some(p => p.codeError) && pieces.some(p => p.code?.trim() && p.name?.trim());
            case 'reps': return Number(repetitions) >= 1;
            case 'impresion': return isPrinting || hasCut; // al menos un proceso
            default: return true;
        }
    };
    const goNext = () => { if (stepValid(curKey)) setWizardStep((s) => Math.min(s + 1, wizardSteps.length - 1)); };
    const goBack = () => setWizardStep((s) => Math.max(s - 1, 0));

    // Resumen de lo que va en el JSON (para el diálogo y el paso "Revisar"). "Hoja" usa
    // resolveSheetDimensionsIn — la MISMA función que doDownloadJson — para que el número que ve
    // el operador acá sea exactamente el que el JSON va a emitir (Gang Run o catálogo, ya
    // normalizado a apaisado), no el valor crudo del catálogo/job.
    const jsonSummaryRows = () => {
        const m = materials.find(mat => mat.id === selectedMaterialId);
        const dims = resolveSheetDimensionsIn(jobData, m);
        return [
            ['Nombre del archivo', generatedFileName.front || hri || '—'],
            ['Material', m ? materialOptionLabel(m) : '—'],
            ['Repeticiones', String(repetitions)],
            ['Procesos', `${isPrinting ? `Impresión ${printFaces} cara(s)` : ''}${isPrinting && hasCut ? ' + ' : ''}${hasCut ? 'Corte' : ''}` || '—'],
            ['Piezas', String(pieces.filter(p => p.code?.trim()).length)],
            ...(dims.width != null ? [['Hoja', `${dims.width} × ${dims.height} in`]] : []),
        ];
    };

    // Bloques de campos reutilizados por el asistente (mismo JSX que la vista clásica).
    const fldBloque = hasGroupings && (
        <div><label className="block text-sm font-medium text-gray-700">Bloque <span className="text-red-500">*</span></label>
            <select value={selectedBlock} onChange={(e) => setSelectedBlock(e.target.value)} className={`mt-1 block w-full pl-3 pr-10 py-2 text-base sm:text-sm rounded-md border ${!selectedGrouping ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}>
                <option value="">Seleccione bloque</option>
                {groupingOptions.blocks.map(b => <option key={`wb-${b.id || b.name}`} value={b.name}>{b.name}{b.code ? ` (${b.code})` : ''}</option>)}
            </select>
        </div>
    );
    const fldMaterial = (
        <div><label className="block text-sm font-medium text-gray-700">Material <span className="text-red-500">*</span></label>
            <div className="flex items-center space-x-2">
                <select value={selectedMaterialId} onChange={(e) => setSelectedMaterialId(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 sm:text-sm rounded-md">
                    <option value="">Seleccione material</option>
                    {selectableMaterials.map(m => (<option key={m.id} value={m.id}>{materialOptionLabel(m)}</option>))}
                </select>
                <button onClick={() => setIsMaterialModalOpen(true)} className="p-2 bg-gray-200 rounded-md hover:bg-gray-300"><Plus size={16} /></button>
            </div>
        </div>
    );
    const fldReps = (
        <div><label className="block text-sm font-medium text-gray-700">Repeticiones (cantidad de planchas)</label>
            <input type="number" min="1" value={repetitions} onChange={(e) => setRepetitions(e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm" />
        </div>
    );
    const procesoCard = (active, onToggle, titulo, children) => (
        <button type="button" onClick={onToggle} className={`flex-1 text-left p-3 rounded-lg border-2 transition ${active ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
            <span className="flex items-center gap-2 font-semibold text-gray-800">
                <span className={`w-4 h-4 rounded border flex items-center justify-center ${active ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-400'}`}>{active ? '✓' : ''}</span>
                {titulo}
            </span>
            {active && children}
        </button>
    );
    const fldImpresionEstado = (
        <div className="space-y-5">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">¿Qué procesos lleva la plancha?</label>
                <p className="text-xs text-gray-500 mb-2">Marca uno o ambos (puede ser solo impresión, solo corte, o impresión + corte).</p>
                <div className="flex gap-3">
                    {procesoCard(isPrinting, () => setIsPrinting(!isPrinting), 'Impresión',
                        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                            <label className="block text-xs text-gray-500 mb-1">Caras a imprimir</label>
                            <select value={printFaces} onChange={(e) => setPrintFaces(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 rounded-md"><option value={1}>1 Cara</option><option value={2}>2 Caras</option></select>
                        </div>
                    )}
                    {procesoCard(hasCut, () => setHasCut(!hasCut), 'Corte',
                        <p className="mt-2 text-xs text-gray-500">Lleva troquel / corte.</p>
                    )}
                </div>
                {!isPrinting && !hasCut && <p className="mt-2 text-xs text-amber-600">Selecciona al menos un proceso (impresión y/o corte).</p>}
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estado de la Plancha</label>
                <div className="flex items-center space-x-4">
                    <label className="flex items-center"><input type="radio" name="wstatus" value="Listo para Prepress" checked={status === 'Listo para Prepress'} onChange={(e) => setStatus(e.target.value)} className="h-4 w-4" /><span className="ml-2 text-sm">Listo para Prepress</span></label>
                    <label className="flex items-center"><input type="radio" name="wstatus" value="Hold" checked={status === 'Hold'} onChange={(e) => setStatus(e.target.value)} className="h-4 w-4" /><span className="ml-2 text-sm">Hold</span></label>
                </div>
            </div>
        </div>
    );
    const fldPiezas = (
        <div className="space-y-3">
            <div className="flex justify-between items-center"><h3 className="font-semibold text-gray-700 flex-grow">Piezas en la Plancha <span className="text-red-500">*</span></h3>{!isGang && <button onClick={addPiece} className="flex items-center space-x-2 px-3 py-1.5 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700"><Plus size={16} /><span>Añadir Pieza</span></button>}</div>
            {piecesIncomplete && (<div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-md text-sm"><AlertCircle size={18} className="flex-shrink-0 mt-0.5" /><span>Registra <strong>al menos una pieza</strong> con <strong>código y nombre</strong>.</span></div>)}
            <div className="overflow-x-auto border rounded-lg"><table className="min-w-full divide-y divide-gray-200"><thead className="bg-gray-50"><tr><th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase w-20">Preview</th><th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">QR</th><th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Código</th><th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Nombre</th><th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Cant. x Plancha</th><th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Total</th><th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Acción</th></tr></thead><tbody className="bg-white divide-y divide-gray-200">{pieces.map(p => (<PieceRow key={p.id} piece={p} onUpdate={updatePiece} onRemove={removePiece} allPiecesMasterList={allPiecesMasterList} jobData={jobData} repetitions={repetitions} onDownloadQrArt={handleDownloadQrArt} onDownloadQrPng={handleDownloadQrPng} />))}</tbody></table></div>
        </div>
    );
    // Fuente ÚNICA del enlace de descarga + nota de versión: se usa tanto en el paso
    // "Revisar" del wizard como en la vista clásica/edición — si el diseñador descarga
    // desde cualquiera de los dos, ve la misma nota de versión (no solo desde el wizard).
    const scriptLink = (
      <div className="flex flex-col gap-1">
        <a href={`${process.env.PUBLIC_URL}/scripts/Cartonbar_design_2.jsx`} download className="inline-flex items-center gap-2 text-sm px-3 py-2 border border-indigo-300 text-indigo-700 rounded-md hover:bg-indigo-50 w-fit">
            <Download size={16} /> Descargar script de Illustrator (Cartonbar_design_2.jsx) — una vez
        </a>
        <p className="text-xs font-bold text-red-600 max-w-xs animate-blink-warning">
            ⚠ Script v{SCRIPT_VERSION} — Cartonbar_design_2.jsx. Descárgalo, BORRA Cartonbar_design_1.jsx de tu carpeta de Scripts, y reinicia Illustrator. Si dejas el viejo, seguirá apareciendo en el menú y produce medidas incorrectas sin avisar.
        </p>
      </div>
    );
    const fileUploadersBlock = (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FileUploader title="Frente" onPaste={handlePaste(setPreviewImageFront)} onDrop={handleDrop(setPreviewImageFront)} onDragOver={handleDragOver} previewImage={previewImageFront} onFileChange={handleFileChange(setDesignFileFront)} uploadProgress={uploadProgressFront} onDownloadJson={() => openJsonConfirm('front')} isDownloadDisabled={!generatedFileName.front} selectedFileName={designFileFront?.name || uploadedFileFrontInfo?.name} hideJsonStep />
            {isPrinting && printFaces == 2 && (<FileUploader title="Dorso" onPaste={handlePaste(setPreviewImageBack)} onDrop={handleDrop(setPreviewImageBack)} onDragOver={handleDragOver} previewImage={previewImageBack} onFileChange={handleFileChange(setDesignFileBack)} uploadProgress={uploadProgressBack} onDownloadJson={() => openJsonConfirm('back')} isDownloadDisabled={!generatedFileName.back} selectedFileName={designFileBack?.name || uploadedFileBackInfo?.name} hideJsonStep />)}
        </div>
    );
    const numeroNombre = (
        <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="block text-xs text-gray-500">Número</span><span className="font-mono font-semibold">{sheetNumber ? String(sheetNumber).padStart(3, '0') : '—'}</span></div>
            <div><span className="block text-xs text-gray-500">Nombre de plancha</span><span className="font-mono">{hri || '—'}</span></div>
        </div>
    );

    const renderWizardStep = () => {
        switch (curKey) {
            case 'bloque': return <div className="space-y-3"><p className="text-sm text-gray-500">Elige el bloque/componente al que pertenece esta plancha (define la numeración).</p>{fldBloque}{numeroNombre}</div>;
            case 'material': return <div className="space-y-4">{fldMaterial}{numeroNombre}</div>;
            case 'piezas': return fldPiezas;
            case 'reps': return <div className="space-y-4 max-w-sm">{fldReps}</div>;
            case 'impresion': return <div className="max-w-md">{fldImpresionEstado}</div>;
            case 'revisar': return (
                <div className="space-y-4">
                    <h3 className="font-semibold text-gray-700">Revisa antes de generar el JSON</h3>
                    <div className="border rounded-lg divide-y">
                        {jsonSummaryRows().map(([k, v]) => (<div key={k} className="flex justify-between px-3 py-2 text-sm"><span className="text-gray-500">{k}</span><span className="font-medium text-gray-800">{v}</span></div>))}
                    </div>
                    <p className="text-xs text-gray-500">Al descargar el JSON se <b>fija el número</b> de plancha. En Illustrator, guarda el .ai con ese nombre.</p>
                    <div className="flex flex-wrap gap-3 items-center">
                        <button onClick={() => doDownloadJson('front')} disabled={!generatedFileName.front} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-green-600 text-white font-semibold text-sm hover:bg-green-700 disabled:bg-gray-400"><FileDown size={16} /> Descargar JSON {isPrinting && printFaces == 2 ? '(Frente)' : ''}</button>
                        {isPrinting && printFaces == 2 && <button onClick={() => doDownloadJson('back')} disabled={!generatedFileName.back} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-green-600 text-white font-semibold text-sm hover:bg-green-700 disabled:bg-gray-400"><FileDown size={16} /> Descargar JSON (Dorso)</button>}
                        {scriptLink}
                    </div>
                </div>
            );
            case 'subir': return (
                <div className="space-y-4">
                    <h3 className="font-semibold text-gray-700">Sube el archivo final de Illustrator (.ai) y guarda</h3>
                    {fileUploadersBlock}
                </div>
            );
            default: return null;
        }
    };

    if (isWizard) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col">
                    <div className="flex justify-between items-center p-4 border-b">
                        <h2 className="text-xl font-bold text-gray-800">Crear Nueva Plancha</h2>
                        <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={20} /></button>
                    </div>
                    {/* Stepper */}
                    <div className="flex items-center gap-1 px-4 py-3 border-b bg-gray-50 overflow-x-auto">
                        {wizardSteps.map((st, i) => (
                            <React.Fragment key={st.key}>
                                <div className={`flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap ${i === curStepIdx ? 'text-blue-700' : i < curStepIdx ? 'text-emerald-600' : 'text-gray-400'}`}>
                                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${i === curStepIdx ? 'bg-blue-600 text-white' : i < curStepIdx ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'}`}>{i + 1}</span>
                                    {st.label}
                                </div>
                                {i < wizardSteps.length - 1 && <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="overflow-y-auto p-6 max-h-[60vh]">{renderWizardStep()}</div>
                    <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
                        <button onClick={curStepIdx === 0 ? onClose : goBack} className="inline-flex items-center gap-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                            <ChevronLeft size={16} /> {curStepIdx === 0 ? 'Cancelar' : 'Atrás'}
                        </button>
                        <div className="flex items-center gap-2">
                            {!stepValid(curKey) && <span className="text-xs text-amber-600">Completa este paso para continuar</span>}
                            {curKey !== 'subir' ? (
                                <button onClick={goNext} disabled={!stepValid(curKey)} className="inline-flex items-center gap-1 px-5 py-2 bg-blue-600 text-white font-bold rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed">Siguiente <ChevronRight size={16} /></button>
                            ) : (
                                <button onClick={handleSave} disabled={isSaving || isUploading || !uploadOk || !stepValid('piezas')} className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-md hover:bg-emerald-700 disabled:bg-gray-400">{isSaving ? 'Guardando…' : (isUploading || !uploadOk ? 'Subiendo…' : 'Crear Plancha')}</button>
                            )}
                        </div>
                    </div>
                </div>
                <MaterialModal isOpen={isMaterialModalOpen} onClose={() => setIsMaterialModalOpen(false)} onSave={handleSaveMaterialInline} db={db} />
                <ConfirmDialog open={jsonConfirm.open} title="Descargar JSON para Illustrator" confirmText="Descargar JSON" tone="success" onCancel={() => setJsonConfirm({ ...jsonConfirm, open: false })} onConfirm={() => { const ft = jsonConfirm.fileType; setJsonConfirm({ ...jsonConfirm, open: false }); doDownloadJson(ft); }}>
                    <p className="mb-3 text-gray-600">Verifica los datos. Al continuar se <b>fija el número</b> y se descarga el archivo para correr en Illustrator.</p>
                    <div className="border rounded-lg divide-y">{jsonSummaryRows().map(([k, v]) => (<div key={k} className="flex justify-between px-3 py-2"><span className="text-gray-500">{k}</span><span className="font-medium text-gray-800">{v}</span></div>))}</div>
                </ConfirmDialog>
            </div>
        );
    }

    return (
         <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-xl font-bold text-gray-800">
                        {taskId ? 'Corregir Plancha' : (currentInitialSheet ? (viewMode ? 'Ver Plancha' : 'Editar Plancha') : 'Crear Nueva Plancha')}
                    </h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={20}/></button>
                </div>
                <div className="flex-grow overflow-y-auto p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-1 space-y-3">
                            <h3 className="font-semibold text-gray-700 border-b pb-2">Información General</h3>
                            {hasGroupings && (
                            <div><label className="block text-sm font-medium text-gray-700">Bloque {!currentInitialSheet && <span className="text-red-500">*</span>}</label>
                                {currentInitialSheet ? (
                                    // Al editar/corregir, el bloque queda BLOQUEADO: cambiarlo alteraría el número/nombre.
                                    <input type="text" readOnly title="No se puede cambiar al editar: rompería el número/nombre de la plancha" value={selectedGrouping ? `${selectedGrouping.name}${selectedGrouping.code && selectedGrouping.code !== selectedGrouping.name ? ` (${selectedGrouping.code})` : ''}` : (selectedBlock || 'Sin bloque')} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm bg-gray-100 text-gray-600 cursor-not-allowed" />
                                ) : (
                                    <select value={selectedBlock} onChange={(e) => setSelectedBlock(e.target.value)} className={`mt-1 block w-full pl-3 pr-10 py-2 text-base focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md border ${!selectedGrouping ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`} disabled={viewMode}>
                                        <option value="">Seleccione bloque</option>
                                        {groupingOptions.blocks.map(b => <option key={`b-${b.id || b.name}`} value={b.name}>{b.name}{b.code ? ` (${b.code})` : ''}</option>)}
                                    </select>
                                )}
                            </div>
                            )}
                            <div><label className="block text-sm font-medium text-gray-700">Número de Plancha</label><input type="text" value={sheetNumber ? String(sheetNumber).padStart(3, '0') : ''} readOnly className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm bg-gray-100 cursor-not-allowed" placeholder={currentInitialSheet ? '' : (hasGroupings ? 'Seleccione bloque/componente para asignar' : 'Se asignará automáticamente')}/></div>
                            <div><label className="block text-sm font-medium text-gray-700">Nombre de Plancha</label><input type="text" value={hri} readOnly className="mt-1 block w-full bg-gray-100 border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm font-mono" placeholder="Se genera al asignar bloque/componente y número"/></div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Material</label>
                                <div className="flex items-center space-x-2">
                                    <select value={selectedMaterialId} onChange={(e) => setSelectedMaterialId(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" disabled={viewMode}>
                                        <option value="">Seleccione material</option>
                                        {selectableMaterials.map(m => ( <option key={m.id} value={m.id}>{materialOptionLabel(m)}</option> ))}
                                    </select>
                                    {!viewMode && <button onClick={() => setIsMaterialModalOpen(true)} className="p-2 bg-gray-200 rounded-md hover:bg-gray-300"><Plus size={16}/></button>}
                                </div>
                                <MaterialModal isOpen={isMaterialModalOpen} onClose={() => setIsMaterialModalOpen(false)} onSave={handleSaveMaterialInline} db={db} />
                            </div>
                            <div><label className="block text-sm font-medium text-gray-700">Repeticiones</label><input type="number" min="1" value={repetitions} onChange={(e) => setRepetitions(e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" disabled={viewMode}/></div>
                            <div className="space-y-2"><label className="flex items-center"><input type="checkbox" checked={isPrinting} onChange={(e) => setIsPrinting(e.target.checked)} className="h-4 w-4 text-indigo-600 border-gray-300 rounded" disabled={viewMode}/><span className="ml-2 text-sm font-medium text-gray-700">¿Impresión?</span></label>{isPrinting && (<div><label className="block text-sm font-medium text-gray-700">Caras a Imprimir</label><select value={printFaces} onChange={(e) => setPrintFaces(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 sm:text-sm rounded-md" disabled={viewMode}><option value={1}>1 Cara</option><option value={2}>2 Caras</option></select></div>)}<label className="flex items-center"><input type="checkbox" checked={hasCut} onChange={(e) => setHasCut(e.target.checked)} className="h-4 w-4 text-indigo-600 border-gray-300 rounded" disabled={viewMode}/><span className="ml-2 text-sm font-medium text-gray-700">¿Corte?</span></label></div>
                        </div>
                        <div className="md:col-span-2 space-y-3">
                            <div className="flex justify-between items-center"><h3 className="font-semibold text-gray-700 border-b pb-2 flex-grow">Piezas en la Plancha <span className="text-red-500">*</span></h3>{!viewMode && !isGang && <button onClick={addPiece} className="flex items-center space-x-2 px-3 py-1.5 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700"><Plus size={16}/><span>Añadir Pieza</span></button>}</div>
                            {!viewMode && piecesIncomplete && (
                                <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-md text-sm">
                                    <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                                    <span>Debes registrar <strong>al menos una pieza</strong> con su <strong>código y nombre</strong>. Es obligatorio para alimentar el inventario.</span>
                                </div>
                            )}
                            <div className="overflow-x-auto border rounded-lg"><table className="min-w-full divide-y divide-gray-200"><thead className="bg-gray-50"><tr><th scope="col" className="px-2 py-2 text-center align-middle text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Preview</th><th scope="col" className="px-2 py-2 text-center align-middle text-xs font-medium text-gray-500 uppercase tracking-wider">QR</th><th scope="col" className="px-2 py-2 text-center align-middle text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th><th scope="col" className="px-2 py-2 text-center align-middle text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th><th scope="col" className="px-2 py-2 text-center align-middle text-xs font-medium text-gray-500 uppercase tracking-wider">Cant. x Plancha</th><th scope="col" className="px-2 py-2 text-center align-middle text-xs font-medium text-gray-500 uppercase tracking-wider">Total Piezas</th><th scope="col" className="px-2 py-2 text-center align-middle text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th></tr></thead><tbody className="bg-white divide-y divide-gray-200">{pieces.map(p => (<PieceRow key={p.id} piece={p} onUpdate={updatePiece} onRemove={removePiece} allPiecesMasterList={allPiecesMasterList} jobData={jobData} repetitions={repetitions} onDownloadQrArt={handleDownloadQrArt} onDownloadQrPng={handleDownloadQrPng} />))}</tbody></table></div>
                            <div className="pt-3 border-t">
                                {scriptLink}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                                <FileUploader title="Frente" onPaste={handlePaste(setPreviewImageFront)} onDrop={handleDrop(setPreviewImageFront)} onDragOver={handleDragOver} previewImage={previewImageFront} onFileChange={handleFileChange(setDesignFileFront)} uploadProgress={uploadProgressFront} onDownloadJson={() => openJsonConfirm('front')} isDownloadDisabled={!generatedFileName.front} selectedFileName={designFileFront?.name || uploadedFileFrontInfo?.name}/>
                                {isPrinting && printFaces == 2 && (<FileUploader title="Dorso" onPaste={handlePaste(setPreviewImageBack)} onDrop={handleDrop(setPreviewImageBack)} onDragOver={handleDragOver} previewImage={previewImageBack} onFileChange={handleFileChange(setDesignFileBack)} uploadProgress={uploadProgressBack} onDownloadJson={() => openJsonConfirm('back')} isDownloadDisabled={!generatedFileName.back} selectedFileName={designFileBack?.name || uploadedFileBackInfo?.name}/>)}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="p-4 border-t bg-gray-50">
                    <div className="flex justify-between items-center">
                        <div>
                            {taskId ? (
                                <div className="flex items-center gap-3 p-2 bg-purple-100 text-purple-800 rounded-md">
                                    <AlertCircle size={20} />
                                    <div className="text-sm">
                                        <p><strong>Modo Corrección:</strong> Al guardar, la plancha se enviará a Prepress como <strong>'Corregido por Diseño'</strong>.</p>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Estado de la Plancha</label>
                                    <div className="flex items-center space-x-4">
                                        <label className="flex items-center">
                                            <input type="radio" name="status" value="Listo para Prepress" checked={status === 'Listo para Prepress'} onChange={(e) => setStatus(e.target.value)} className="h-4 w-4" disabled={viewMode}/> 
                                            <span className="ml-2 text-sm">Listo para Prepress</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input type="radio" name="status" value="Hold" checked={status === 'Hold'} onChange={(e) => setStatus(e.target.value)} className="h-4 w-4" disabled={viewMode}/>
                                            <span className="ml-2 text-sm">Hold</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center space-x-3">
                            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300" disabled={isSaving}>Cancelar</button>
                            {!viewMode && (
                                <button onClick={handleSave} title={piecesIncomplete ? 'Registra al menos una pieza con código y nombre' : ''} className="px-4 py-2 bg-blue-600 text-white font-bold rounded-md hover:bg-blue-700 w-48 flex items-center justify-center disabled:bg-gray-400 disabled:cursor-not-allowed" disabled={isSaving || isUploading || !uploadOk || piecesIncomplete || pieces.some(p => p.codeError) || (!currentInitialSheet && hasGroupings && !selectedGrouping)}>
                                    <div className="flex items-center justify-center gap-2">
                                        {isSaving ? 'Guardando...' : (isUploading || !uploadOk ? 'Subiendo...' : (currentInitialSheet || taskId ? 'Actualizar Plancha' : 'Crear Plancha'))}
                                    </div>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmDialog open={jsonConfirm.open} title="Descargar JSON para Illustrator" confirmText="Descargar JSON" tone="success" onCancel={() => setJsonConfirm({ ...jsonConfirm, open: false })} onConfirm={() => { const ft = jsonConfirm.fileType; setJsonConfirm({ ...jsonConfirm, open: false }); doDownloadJson(ft); }}>
                <p className="mb-3 text-gray-600">Verifica los datos. Al continuar se <b>fija el número</b> y se descarga el archivo para correr en Illustrator.</p>
                <div className="border rounded-lg divide-y">{jsonSummaryRows().map(([k, v]) => (<div key={k} className="flex justify-between px-3 py-2"><span className="text-gray-500">{k}</span><span className="font-medium text-gray-800">{v}</span></div>))}</div>
            </ConfirmDialog>
        </div>
    );
};

export default SheetModal;

